import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { mapsApi, type EmbedConfig, type PublicLink } from '../../lib/mapsApi';

/** Renders the iframe at its real declared width/height, then scales the
 * whole thing down uniformly to fit the available panel width — an iframe's
 * `width`/`height` attributes set an absolute pixel box, and capping only
 * `max-width` on it (the previous approach) shrinks the box horizontally
 * while the height attribute stays literal, distorting a normally-wide frame
 * into a tall, narrow-looking one. Scaling preserves the actual aspect ratio
 * at any panel size instead. */
function IframePreview({ src, width, height, title }: { src: string; width: number; height: number; title: string }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      const available = el.clientWidth;
      setScale(available > 0 ? Math.min(1, available / width) : 1);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  return (
    <div ref={wrapperRef} style={{ height: height * scale }}>
      <div style={{ width, height, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        <iframe src={src} width={width} height={height} style={{ border: 0 }} title={title} />
      </div>
    </div>
  );
}

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`flex h-[26px] w-[52px] shrink-0 items-center rounded-full px-1 transition-colors ${
        checked ? 'bg-action-primary justify-end' : 'bg-bg-surface-secondary justify-start'
      }`}
    >
      <span className="size-[18px] rounded-full bg-bg-surface shadow" />
    </button>
  );
}

/** The "Доступ" tab — a public, unauthenticated read-only view of this map
 * (see /embed/:token, EmbedMapPage.tsx) for embedding elsewhere. Unlike the
 * rest of MapSettingsPage, this tab doesn't go through the page's draft
 * `form`/Save button — every action here (enable/regenerate/toggle/config
 * change) hits its own endpoint immediately, same independent-action pattern
 * as BasemapsTab/LayerGroupsTab. */
export function AccessTab({ mapId, mapTitle }: { mapId: string; mapTitle: string }) {
  const [link, setLink] = useState<PublicLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);

  useEffect(() => {
    mapsApi
      .getPublicLink(mapId)
      .then(setLink)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) setLink(null);
        else setError(err instanceof ApiError ? err.message : 'Не удалось загрузить настройки доступа.');
      })
      .finally(() => setLoading(false));
  }, [mapId]);

  const handleEnable = async () => {
    setBusy(true);
    setError(null);
    try {
      setLink(await mapsApi.enablePublicLink(mapId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось включить публичный доступ.');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async () => {
    if (!link) return;
    setBusy(true);
    setError(null);
    try {
      setLink(await mapsApi.updatePublicLink(mapId, { isActive: !link.isActive }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось изменить доступ.');
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Старая ссылка перестанет работать везде, где уже вставлена. Продолжить?')) return;
    setBusy(true);
    setError(null);
    try {
      setLink(await mapsApi.regeneratePublicLink(mapId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось обновить ссылку.');
    } finally {
      setBusy(false);
    }
  };

  const handleConfigChange = async (embedConfig: EmbedConfig) => {
    if (!link) return;
    const prev = link;
    setLink({ ...link, embedConfig });
    try {
      await mapsApi.updatePublicLink(mapId, { embedConfig });
    } catch (err) {
      setLink(prev);
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить настройки фрейма.');
    }
  };

  const embedUrl = link ? `${window.location.origin}/embed/${link.token}` : '';
  const iframeCode = link
    ? `<iframe src="${embedUrl}" width="${link.embedConfig.width}" height="${link.embedConfig.height}" style="border:0" title="${mapTitle.replace(/"/g, '&quot;')}"></iframe>`
    : '';

  const copy = async (text: string, which: 'link' | 'code') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // буфер обмена недоступен — тихо игнорируем
    }
  };

  if (loading) {
    return <div className="px-8 py-16 text-center text-sm text-text-secondary">Загрузка…</div>;
  }

  return (
    <div className="px-8 py-6">
      <div className="mx-auto max-w-[900px]">
        {error && <p className="mb-4 text-xs font-medium text-status-danger">{error}</p>}

        {!link ? (
          <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
            <h2 className="text-[15px] font-semibold text-text-primary">Публичный доступ по ссылке</h2>
            <p className="mt-1 text-xs text-text-secondary">
              Отдельная read-only страница с этой картой, без входа в систему — можно вставить как iframe на другой
              сайт. Поиск и редактирование там недоступны; отображаются только опубликованные векторные слои.
            </p>
            <button
              type="button"
              onClick={handleEnable}
              disabled={busy}
              className="mt-4 flex h-10 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Включаем…' : 'Включить публичный доступ'}
            </button>
          </section>
        ) : (
          <div className="grid grid-cols-[380px_1fr] gap-6">
            <div className="flex flex-col gap-6">
              <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-[15px] font-semibold text-text-primary">Ссылка</h2>
                  <span
                    className={`flex h-6 items-center rounded-lg px-2.5 text-[11px] font-medium ${
                      link.isActive ? 'bg-status-success text-text-inverse' : 'bg-bg-surface-secondary text-text-secondary'
                    }`}
                  >
                    {link.isActive ? 'Включена' : 'Отключена'}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    readOnly
                    value={embedUrl}
                    className="h-10 flex-1 rounded-[9px] border border-border-default bg-bg-canvas px-3 text-[11px] text-text-primary outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => copy(embedUrl, 'link')}
                    className="flex h-10 shrink-0 items-center rounded-[10px] border border-border-default bg-bg-surface px-3 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                  >
                    {copied === 'link' ? 'Скопировано ✓' : 'Копировать'}
                  </button>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={handleToggleActive}
                    disabled={busy}
                    className="flex h-9 items-center rounded-[9px] border border-border-default bg-bg-surface px-3 text-[12px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {link.isActive ? 'Отключить доступ' : 'Включить обратно'}
                  </button>
                  <button
                    type="button"
                    onClick={handleRegenerate}
                    disabled={busy}
                    className="flex h-9 items-center rounded-[9px] border border-border-default bg-bg-surface px-3 text-[12px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Обновить ссылку
                  </button>
                </div>
              </section>

              <section className="rounded-2xl border border-border-default bg-bg-surface p-6">
                <h2 className="text-[15px] font-semibold text-text-primary">Конструктор iframe</h2>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="flex flex-col">
                    <label className="text-[11px] font-medium text-text-secondary">Ширина, px</label>
                    <input
                      type="number"
                      min={200}
                      max={4000}
                      value={link.embedConfig.width}
                      onChange={(e) => handleConfigChange({ ...link.embedConfig, width: Number(e.target.value) })}
                      className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[11px] font-medium text-text-secondary">Высота, px</label>
                    <input
                      type="number"
                      min={150}
                      max={4000}
                      value={link.embedConfig.height}
                      onChange={(e) => handleConfigChange({ ...link.embedConfig, height: Number(e.target.value) })}
                      className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
                    />
                  </div>
                </div>

                <div className="mt-4 flex flex-col gap-3">
                  <label className="flex items-center justify-between gap-4">
                    <span className="text-[11px] font-medium text-text-secondary">Показывать легенду</span>
                    <ToggleSwitch
                      checked={link.embedConfig.showLegend}
                      onChange={() => handleConfigChange({ ...link.embedConfig, showLegend: !link.embedConfig.showLegend })}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-4">
                    <span className="text-[11px] font-medium text-text-secondary">Переключатель подложек</span>
                    <ToggleSwitch
                      checked={link.embedConfig.showBasemapSwitcher}
                      onChange={() =>
                        handleConfigChange({ ...link.embedConfig, showBasemapSwitcher: !link.embedConfig.showBasemapSwitcher })
                      }
                    />
                  </label>
                </div>

                <label className="mt-4 block text-[11px] font-medium text-text-secondary">Код для вставки</label>
                <textarea
                  readOnly
                  value={iframeCode}
                  rows={3}
                  className="mt-1.5 w-full resize-none rounded-[9px] border border-border-default bg-bg-canvas px-3 py-2 text-[11px] text-text-primary outline-none"
                />
                <button
                  type="button"
                  onClick={() => copy(iframeCode, 'code')}
                  className="mt-2 flex h-9 items-center rounded-[9px] border border-border-default bg-bg-surface px-3 text-[12px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                >
                  {copied === 'code' ? 'Скопировано ✓' : 'Скопировать код'}
                </button>
              </section>
            </div>

            <section className="flex h-fit flex-col rounded-2xl border border-border-default bg-bg-surface p-4">
              <h2 className="text-[13px] font-semibold text-text-primary">Предпросмотр</h2>
              <div className="mt-3 rounded-xl border border-border-default bg-bg-canvas p-3">
                {link.isActive ? (
                  <IframePreview
                    key={link.token}
                    src={embedUrl}
                    width={link.embedConfig.width}
                    height={link.embedConfig.height}
                    title="Предпросмотр встраиваемой карты"
                  />
                ) : (
                  <p className="p-4 text-center text-xs text-text-secondary">
                    Доступ отключён — включите его снова, чтобы увидеть предпросмотр
                  </p>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
